import React, { useCallback, useEffect, useState, createContext, useContext } from 'react';
import { Product } from '../data/products';
import { useStore } from './StoreContext';
import { YarboCarePlanId, getPlanById, isEligibleForYarboCare } from '../data/yarboCare';
export interface CartItem {
  product: Product;
  quantity: number;
  protectionPlanId?: YarboCarePlanId;
}
interface StoredCartItem {
  productId: string;
  quantity: number;
  protectionPlanId?: YarboCarePlanId;
}
export type ShippingMethod = 'standard' | 'expedited' | 'pickup' | null;
interface CartContextType {
  items: CartItem[];
  itemCount: number;
  productSubtotal: number;
  protectionSubtotal: number;
  discountAmount: number;
  shippingEstimate: number | null;
  taxEstimate: number | null;
  subtotal: number;
  total: number;
  isOpen: boolean;
  lastAdded: Product | null;
  discountCode: string | null;
  shippingMethod: ShippingMethod;
  // YarboCare modal state
  yarboCareModalOpen: boolean;
  yarboCareTargetProductId: string | null;
  closeYarboCareModal: () => void;
  addToCart: (product: Product, quantity?: number) => void;
  removeFromCart: (productId: string) => void;
  updateQuantity: (productId: string, quantity: number) => void;
  attachProtection: (productId: string, planId: YarboCarePlanId) => void;
  removeProtection: (productId: string) => void;
  applyDiscount: (code: string, amount: number) => void;
  removeDiscount: () => void;
  setShippingMethod: (method: ShippingMethod) => void;
  clearCart: () => void;
  openCart: () => void;
  closeCart: () => void;
}
const CartContext = createContext<CartContextType | undefined>(undefined);
const CART_STORAGE_KEY = 'yarbo_cart';
const DISCOUNT_STORAGE_KEY = 'yarbo_cart_discount';
export function CartProvider({
  children
}: {
  children: ReactNode;
}) {
  const {
    products
  } = useStore();
  const [items, setItems] = useState<CartItem[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [lastAdded, setLastAdded] = useState<Product | null>(null);
  const [yarboCareModalOpen, setYarboCareModalOpen] = useState(false);
  const [yarboCareTargetProductId, setYarboCareTargetProductId] = useState<string | null>(null);
  const [discountCode, setDiscountCode] = useState<string | null>(null);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [shippingMethod, setShippingMethod] = useState<ShippingMethod>(null);
  // Load from localStorage on mount
  useEffect(() => {
    if (products.length === 0) return; // Wait for products to load
    try {
      const stored = localStorage.getItem(CART_STORAGE_KEY);
      if (stored) {
        const parsed: StoredCartItem[] = JSON.parse(stored);
        const resolvedItems = parsed.map(item => {
          const product = products.find(p => p.id === item.productId);
          if (product) {
            return {
              product,
              quantity: item.quantity,
              protectionPlanId: item.protectionPlanId
            };
          }
          return null;
        }).filter(Boolean) as CartItem[];
        setItems(resolvedItems);
      }
      const storedDiscount = localStorage.getItem(DISCOUNT_STORAGE_KEY);
      if (storedDiscount) {
        const {
          code,
          amount
        } = JSON.parse(storedDiscount);
        setDiscountCode(code);
        setDiscountAmount(amount);
      }
    } catch (e) {
      console.error('Failed to load cart from localStorage', e);
    }
  }, [products]);
  // Save to localStorage on change
  useEffect(() => {
    const storedItems: StoredCartItem[] = items.map(item => ({
      productId: item.product.id,
      quantity: item.quantity,
      protectionPlanId: item.protectionPlanId
    }));
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(storedItems));
  }, [items]);
  const addToCart = useCallback((product: Product, quantity = 1) => {
    setItems(prev => {
      const existing = prev.find(item => item.product.id === product.id);
      if (existing) {
        return prev.map(item => item.product.id === product.id ? {
          ...item,
          quantity: item.quantity + quantity
        } : item);
      }
      return [...prev, {
        product,
        quantity
      }];
    });
    setLastAdded(product);
    // If eligible, prompt YarboCare modal; otherwise just open the drawer
    if (isEligibleForYarboCare(product) || product.eligibleForYarboCare) {
      setYarboCareTargetProductId(product.id);
      setYarboCareModalOpen(true);
    } else {
      setIsOpen(true);
    }
  }, []);
  const removeFromCart = useCallback((productId: string) => {
    setItems(prev => prev.filter(item => item.product.id !== productId));
  }, []);
  const updateQuantity = useCallback((productId: string, quantity: number) => {
    if (quantity <= 0) {
      setItems(prev => prev.filter(item => item.product.id !== productId));
      return;
    }
    setItems(prev => prev.map(item => item.product.id === productId ? {
      ...item,
      quantity
    } : item));
  }, []);
  const attachProtection = useCallback((productId: string, planId: YarboCarePlanId) => {
    setItems(prev => prev.map(item => item.product.id === productId ? {
      ...item,
      protectionPlanId: planId
    } : item));
  }, []);
  const removeProtection = useCallback((productId: string) => {
    setItems(prev => prev.map(item => item.product.id === productId ? {
      ...item,
      protectionPlanId: undefined
    } : item));
  }, []);
  const applyDiscount = useCallback((code: string, amount: number) => {
    setDiscountCode(code);
    setDiscountAmount(amount);
    localStorage.setItem(DISCOUNT_STORAGE_KEY, JSON.stringify({
      code,
      amount
    }));
  }, []);
  const removeDiscount = useCallback(() => {
    setDiscountCode(null);
    setDiscountAmount(0);
    localStorage.removeItem(DISCOUNT_STORAGE_KEY);
  }, []);
  const closeYarboCareModal = useCallback(() => {
    setYarboCareModalOpen(false);
    setYarboCareTargetProductId(null);
    setIsOpen(true); // hand off to the cart drawer
  }, []);
  const clearCart = useCallback(() => {
    setItems([]);
    removeDiscount();
    setShippingMethod(null);
  }, [removeDiscount]);
  const openCart = useCallback(() => setIsOpen(true), []);
  const closeCart = useCallback(() => setIsOpen(false), []);
  const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);
  const productSubtotal = items.reduce((sum, item) => sum + item.product.price * item.quantity, 0);
  const protectionSubtotal = items.reduce((sum, item) => {
    const plan = getPlanById(item.protectionPlanId);
    return sum + (plan ? plan.priceMockup : 0);
  }, 0);
  const subtotal = productSubtotal + protectionSubtotal;
  let shippingEstimate: number | null = null;
  if (shippingMethod === 'expedited') shippingEstimate = 249;else if (shippingMethod === 'pickup') shippingEstimate = 0;
  const taxEstimate = subtotal > 0 ? (subtotal - discountAmount) * 0.06 : null;
  const total = Math.max(0, subtotal - discountAmount + (shippingEstimate || 0) + (taxEstimate || 0));
  return <CartContext.Provider value={{
    items,
    itemCount,
    productSubtotal,
    protectionSubtotal,
    discountAmount,
    shippingEstimate,
    taxEstimate,
    subtotal,
    total,
    isOpen,
    lastAdded,
    discountCode,
    shippingMethod,
    yarboCareModalOpen,
    yarboCareTargetProductId,
    closeYarboCareModal,
    addToCart,
    removeFromCart,
    updateQuantity,
    attachProtection,
    removeProtection,
    applyDiscount,
    removeDiscount,
    setShippingMethod,
    clearCart,
    openCart,
    closeCart
  }} data-id="element-314">
      {children}
    </CartContext.Provider>;
}
export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
}